// Packages
import React, { useEffect, useRef, useContext } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"

//	Configs
import ToggleConfig from "components/Stacker/config/ToggleConfig"
import ProductDetailsConfig from "components/Stacker/config/ProductDetailsConfig"

// Actions
import { refineSwatch } from "components/Stacker/actions/StackerActions"
import { selectSwatch } from "components/Stacker/actions/SwatchActions"
import { getPriceAndInventory } from "components/Stacker/actions/ProductDetailActions"

//	Components
import SwatchSet from "components/Stacker/components/SwatchSet/SwatchSet"
import DropDown from "components/Stacker/components/DropDown/DropDown"

//	Styles
import "./ProductSwatches.scss"
import { usePrevious } from "reactHooks"
import { useDispatch } from "react-redux"
import { StackerContext } from "components/DYStackers/StackerContext"
import { useI18nContext } from "i18n/i18n-react"

function ProductSwatches({
  pid,
  selectedVariant,
  selectedVariant: { code },
  variantOptions,
  selectedVariantIndex,
  selectedSize,
  baseStore,
  shouldUpdateDataLayer,
  hideSwatches,
  isAlphabetStone,
  isSwatchDropDown,
  shouldUpdateQuickViewLayer,
  isEditCart = false,
  entryNumber,
  refinerId,
}) {
  const initialized = useRef()
  const { LL } = useI18nContext()
  const dispatch = useDispatch()
  const prevCode = usePrevious(code)
  const stackerContext = useContext(StackerContext)
  useEffect(() => {
    if (!selectedSize && code) {
      getInventoryCodes({
        productId: code,
        pid,
        variantOptions,
      })
    }
  }, [selectedSize, code])

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true
      return
    }

    if (code !== prevCode) {
      getInventoryCodes({
        productId: code,
        pid,
        variantOptions,
      })
    }
  }, [selectedVariant])

  const getInventoryCodes = (options) => {
    const { pid, variantOptions } = options

    let { productId } = options
    const isEditShoppingBag = isEditCart

    if (variantOptions && variantOptions.length > 0) {
      variantOptions.forEach((option) => {
        productId += `,${option.code}`
      })
    }
    dispatch(
      getPriceAndInventory(
        {
          productId,
          productType: ProductDetailsConfig.BASE_PRODUCT,
          pid,
          baseStore,
          shouldUpdateDataLayer,
          shouldUpdateQuickViewLayer,
          isEditShoppingBag,
        },
        stackerContext.retailerId
      )
    )
  }

  const handleSelection = (variant, index) => {
    const variantCode = variant.code
    const variantIndex = Number(index)
    const swatchData = {
      baseProductCode: pid,
      selectedVariantIndex: variantIndex,
      selectedVariantCode: variantCode,
      isEditCart,
      entryNumber,
    }
    if (refinerId) {
      const selInfo = {
        selectedVariant: variant,
        selectedVariantIndex: variantIndex,
      }
      const R = {
        refinerId,
        selInfo,
        variantCode,
      }
      dispatch(refineSwatch({ data: R, retailerId: stackerContext.retailerId }))
    } else {
      dispatch(selectSwatch(swatchData))
    }
  }

  const formattedDropDownItems = () => {
    const formmatedSwatches = variantOptions.map((swatchObj) => {
      return {
        ...swatchObj,
        label: swatchObj.color,
        icon: swatchObj.colorImage,
      }
    })
    return formmatedSwatches
  }

  const swatchSettings = {
    dots: false,
    speed: 500,
    slickGoTo: 0,
    infinite: false,
    accessibility: true,
    slidesToShow: 10,
    slidesToScroll: 1,
    tabIndex: 0,
  }

  if (hideSwatches) {
    return null
  }

  const dropId = isSwatchDropDown && refinerId ? refinerId : pid
  const toggleId = `${dropId}_${ToggleConfig.SWATCH_DROPDOWN_TOGGLE}`
  const classProduct = classNames("ProductSwatches d-flex align-items-center", {
    "letter-swatches": isAlphabetStone,
  })
  const classList = classNames(
    {
      "color-text": !isAlphabetStone,
      "letter-text": isAlphabetStone,
    },
    "text-black"
  )
  return (
    <div className={classProduct}>
      <div className="col-3 no-padding">
        <label htmlFor="color" className={classList}>
          {isAlphabetStone ? LL.pdp.initialLabel() : LL.generic.colorLabel()}
        </label>
      </div>
      {!isSwatchDropDown && (
        <SwatchSet
          baseProductCode={pid}
          defaultSelectedIndex={selectedVariantIndex}
          entryNumber={entryNumber}
          isEditCart={isEditCart}
          isPDP
          isSlider={false}
          productVariants={variantOptions}
          refinerId={refinerId}
          showTooltip
          swatchSettings={swatchSettings}
          tabIndex="0"
        />
      )}
      {isSwatchDropDown && (
        <div className="col-9 no-padding">
          <DropDown
            isNotFormComponent
            id={toggleId}
            itemAction={handleSelection}
            items={formattedDropDownItems()}
            label={
              isAlphabetStone ? LL.pdp.initialLabel() : LL.generic.colorLabel()
            }
            selectedIcon={selectedVariant.colorImage}
            selectedItem={selectedVariant.color}
          />
        </div>
      )}
    </div>
  )
}

ProductSwatches.propTypes = {
  baseStore: PropTypes.string,
  entryNumber: PropTypes.string,
  hideSwatches: PropTypes.bool,
  isAlphabetStone: PropTypes.bool,
  isEditCart: PropTypes.bool,
  isSwatchDropDown: PropTypes.bool,
  pid: PropTypes.string,
  refinerId: PropTypes.string,
  selectedSize: PropTypes.object,
  selectedVariant: PropTypes.object,
  selectedVariantIndex: PropTypes.number.isRequired,
  shouldUpdateDataLayer: PropTypes.bool,
  shouldUpdateQuickViewLayer: PropTypes.bool,
  variantOptions: PropTypes.array,
}

export default ProductSwatches
